import React from "react";
import { fileViewUrl } from "../lib/api";
import { GraduationCap, IdCard, Phone, Shield, UserRound, Hash, Briefcase } from "lucide-react";

/**
 * Compact profile card for the person (faculty/student) who conducted a visit.
 * Shows avatar (photo or initials), name, role badge, batch / degree / roll number / contact.
 *
 * Props:
 *   - creator: { name, role, batch, degree, roll_number, phone, email, photo_path }
 *   - title?: header label (default "Care Provider")
 *   - compact?: smaller variant for inline use under each visit block
 */
export default function CareProviderCard({ creator, title = "Care Provider", compact = false, testId }) {
  if (!creator) {
    return (
      <div className={`rounded-xl border border-brand-border bg-white ${compact ? "p-3" : "p-5"}`} data-testid={testId}>
        <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-bold">{title}</div>
        <div className="mt-2 text-sm text-brand-muted">Provider details not available.</div>
      </div>
    );
  }

  const initials = (creator.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  const roleLabel = creator.role === "manager" ? "Manager" : creator.role === "faculty" ? "Faculty" : "Student";
  const roleColor =
    creator.role === "manager" ? "#2B5F4E"
    : creator.role === "faculty" ? "#367661"
    : "#D78A76";

  return (
    <div
      className={`rounded-xl border border-brand-border bg-white shadow-tactile ${compact ? "p-3" : "p-5"}`}
      data-testid={testId}
    >
      {!compact && (
        <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-bold mb-3">{title}</div>
      )}

      <div className={`flex items-center gap-3 ${compact ? "" : "gap-4"}`}>
        <Avatar photo={creator.photo_path} initials={initials} size={compact ? 44 : 64} accent={roleColor} />
        <div className="min-w-0 flex-1">
          <div className={`font-heading font-semibold ${compact ? "text-sm" : "text-lg"} truncate`}>{creator.name || "—"}</div>
          <div className="mt-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold text-white" style={{ background: roleColor }}>
            <Shield className="h-3 w-3" /> {roleLabel}
          </div>
        </div>
      </div>

      {!compact && (
        <div className="mt-4 grid grid-cols-1 gap-2 text-sm">
          {creator.designation && <ProfileRow icon={Briefcase} label="Designation" value={creator.designation} />}
          {creator.degree && <ProfileRow icon={GraduationCap} label="Course" value={creator.degree} />}
          {creator.batch  && <ProfileRow icon={UserRound}     label="Batch"  value={creator.batch} />}
          {creator.registration_number && <ProfileRow icon={Hash}  label="Reg #" value={creator.registration_number} />}
          {creator.roll_number && <ProfileRow icon={IdCard}    label="Roll #" value={creator.roll_number} />}
          {creator.phone && <ProfileRow icon={Phone}          label="Phone"  value={creator.phone} />}
        </div>
      )}

      {compact && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-brand-muted">
          {creator.designation && <span><b className="text-brand-ink">Designation:</b> {creator.designation}</span>}
          {creator.degree && <span><b className="text-brand-ink">Course:</b> {creator.degree}</span>}
          {creator.batch  && <span><b className="text-brand-ink">Batch:</b> {creator.batch}</span>}
          {creator.registration_number && <span><b className="text-brand-ink">Reg:</b> {creator.registration_number}</span>}
          {creator.roll_number && <span><b className="text-brand-ink">Roll:</b> {creator.roll_number}</span>}
        </div>
      )}
    </div>
  );
}

function Avatar({ photo, initials, size = 56, accent = "#2B5F4E" }) {
  const dim = `${size}px`;
  if (photo) {
    return (
      <img
        src={fileViewUrl(photo)}
        alt=""
        style={{ width: dim, height: dim }}
        className="rounded-full object-cover border-2 shrink-0"
      />
    );
  }
  return (
    <div
      style={{ width: dim, height: dim, background: `${accent}18`, color: accent }}
      className="rounded-full grid place-items-center font-heading font-semibold border-2 shrink-0"
    >
      <span style={{ fontSize: size / 2.6 }}>{initials || "?"}</span>
    </div>
  );
}

function ProfileRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="h-3.5 w-3.5 text-brand-muted mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <span className="text-[10px] uppercase tracking-wider text-brand-muted font-bold">{label}</span>
        <div className="text-sm font-medium truncate">{value}</div>
      </div>
    </div>
  );
}
